import { useEffect, useState } from "react";
import { TbUserOff } from "react-icons/tb";
import { BasicUser } from "types/topicflow";

import Tooltip from "@components/tooltip/tooltip";
import { classNames } from "@helpers/css";

export type AvatarProps = {
  user?:
    | BasicUser
    | { name: string; email?: string | null; avatar?: null | string }
    | null;
  size?: string | number;
  className?: string;
  disabled?: boolean;
  showTooltip?: boolean;
  unassignedTooltipText?: string;
  tooltipPrefix?: string;
  tooltipSuffix?: string;
};

const getAvatarLabel = (user: AvatarProps["user"]) =>
  user?.name || user?.email || "user";

const getAvatarInitials = (value?: string | null) => {
  const name = value?.trim();

  if (!name) {
    return "?";
  }

  const nameParts = name.split(/\s+/);

  if (nameParts.length === 1) {
    return nameParts[0].slice(0, 2).toUpperCase();
  }

  return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
};

const getAvatarBackgroundColor = (value?: string | null) => {
  const name = value?.trim() || "user";
  const hash = name.split("").reduce((memo, char) => {
    return char.charCodeAt(0) + (memo << 5) - memo;
  }, 0);
  const hue = Math.abs(hash) % 360;

  return `hsl(${hue} 65% 42%)`;
};

const getAvatarFontSize = (size: string | number) => {
  const sizeNumber = Number(size);

  if (!Number.isFinite(sizeNumber)) {
    return undefined;
  }

  return `${Math.max(8, sizeNumber * 1.6)}px`;
};

const Avatar: React.FC<AvatarProps> = ({
  user,
  size = "12",
  className = "",
  disabled = false,
  showTooltip = true,
  unassignedTooltipText = "Unassigned",
  tooltipPrefix = "",
  tooltipSuffix = "",
}) => {
  const [imageLoaded, setImageLoaded] = useState(true);
  const avatarLabel = getAvatarLabel(user);
  const avatarColorSeed = user?.name || user?.email;

  useEffect(() => {
    setImageLoaded(true);
  }, [user?.avatar]);

  const getImage = () => (
    <img
      className={classNames(
        `h-${size} w-${size} block flex-none shrunk-0 text-white bg-gray-400 rounded-full m-0`,
        className,
        disabled ? "grayscale contrast-50" : false,
      )}
      alt={user?.name || user?.email || ""}
      aria-label={`Avatar ${avatarLabel}`}
      src={user?.avatar ?? undefined}
      referrerPolicy="no-referrer"
      onError={(e) => {
        e.currentTarget.onerror = null;
        setImageLoaded(false);
      }}
    />
  );
  // The referrerpolicy attr is from this suggestion to fix broken google avatar images:
  // https://stackoverflow.com/a/61042200
  const ExistingUserIcon = (
    <span
      className={classNames(
        `h-${size} w-${size} flex flex-none shrunk-0 items-center justify-center rounded-full font-semibold leading-none text-white select-none`,
        className,
        disabled ? "grayscale contrast-50" : false,
      )}
      aria-label={`Avatar ${avatarLabel}`}
      style={{
        backgroundColor: getAvatarBackgroundColor(avatarColorSeed),
        fontSize: getAvatarFontSize(size),
      }}
    >
      {getAvatarInitials(avatarColorSeed)}
    </span>
  );
  const NoUserIcon = (
    <TbUserOff
      className={classNames(
        `h-${size} w-${size} block flex-none shrunk-0 rounded-full text-gray-400`,
        className,
      )}
      aria-label={`Avatar ${user?.name || user?.email || "unassigned"}`}
    />
  );
  return user ? (
    user.avatar && imageLoaded ? (
      <>
        {showTooltip ? (
          <Tooltip
            text={`${tooltipPrefix || ""}${user.name || user.email}${
              tooltipSuffix || ""
            }`}
          >
            {getImage()}
          </Tooltip>
        ) : (
          getImage()
        )}
      </>
    ) : (
      <>
        {showTooltip ? (
          <Tooltip
            text={`${tooltipPrefix || ""}${user?.name || user?.email}${
              tooltipSuffix || ""
            }`}
          >
            <span>{ExistingUserIcon}</span>
          </Tooltip>
        ) : (
          ExistingUserIcon
        )}
      </>
    )
  ) : (
    <>
      {showTooltip ? (
        <Tooltip text={unassignedTooltipText}>
          <span className="inline-block">{NoUserIcon}</span>
        </Tooltip>
      ) : (
        NoUserIcon
      )}
    </>
  );
};

Avatar.displayName = "Avatar";

export default Avatar;
